import React from 'react';
import Slider from 'react-slick';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const SliderContainer = styled(Box)(({ theme }) => ({
  marginBottom: '2rem',
  position: 'relative',
  '& .slick-slide': {
    padding: '0'
  },
  '& .slick-prev, & .slick-next': {
    zIndex: 1,
    width: '30px',
    height: '30px',
    bottom: 0,
    top: 'auto',
    transform: 'none',
    '&:before': {
      display: 'none' // Cache l'icône par défaut
    }
  },
  '& .slick-prev': {
    right: '40px',
    left: 'auto'
  },
  '& .slick-next': {
    right: '0'
  },
  '& .slick-dots': {
    display: 'none !important'
  }
}));

const ImageContainer = styled(Box)({
  width: '100%',
  height: '400px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  backgroundColor: '#FFFFFF',
  '& > div': {
    width: '100%',
    height: '100%'
  }
});

const StyledImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'contain'
});

const ArrowButton = styled(Box)(({ theme }) => ({
  width: '30px',
  height: '30px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: theme.palette.grey[700],
  '&:hover': {
    color: theme.palette.primary.main
  }
}));

// Composants flèches personnalisés
const PrevArrow = (props) => (
  <ArrowButton {...props}>
    <ChevronLeft />
  </ArrowButton>
);

const NextArrow = (props) => (
  <ArrowButton {...props}>
    <ChevronRight />
  </ArrowButton>
);

const SectorImageSlider = ({ images, sector, company }) => {
  const settings = {
    dots: false,
    infinite: images.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: images.length > 1,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    adaptiveHeight: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: images.length > 1
        }
      }
    ]
  };

  if (!images || images.length === 0) {
    return null;
  }

  const importImage = (imageName) => {
    try {
      const sectorPath = sector.includes('-') ? sector.split('-')[1] : sector;
      
      // Créer un tableau de variantes possibles du nom de fichier
      const variants = [
        imageName,                              // Original
        imageName.toLowerCase(),                // Tout en minuscules
        imageName.replace('.jpg', '.JPG'),     // Extension en majuscules
        imageName.replace('.JPG', '.jpg')      // Extension en minuscules
      ];

      // Essayer chaque variante
      for (const variant of variants) {
        try {
          const context = require.context('../assets/images/sectors', true, /\.(png|jpe?g|gif|webp|JPG)$/i);
          const imagePath = `./${company}/${sectorPath}/${variant}`;
          return context(imagePath);
        } catch (variantError) {
          continue; // Essayer la prochaine variante
        }
      }
      
      // Si aucune variante n'a fonctionné
      throw new Error(`Aucune variante n'a fonctionné pour ${imageName}`);
    } catch (error) {
      console.error(`Erreur de chargement de l'image: ${imageName}`, error);
      return null;
    }
  };

  return (
    <SliderContainer>
      {images.length === 1 ? (
        <ImageContainer>
          <StyledImage
            src={importImage(images[0].url)}
            alt={images[0].caption || 'Image 1'}
            loading="lazy"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://placehold.co/600x400?text=Image+non+disponible';
            }}
          />
        </ImageContainer>
      ) : (
        <Slider {...settings}>
          {images.map((image, index) => {
            const imageSrc = importImage(image.url);
            if (!imageSrc) return null;

            return (
              <div key={index}>
                <ImageContainer>
                  <StyledImage
                    src={imageSrc}
                    alt={image.caption || `Image ${index + 1}`}
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://placehold.co/600x400?text=Image+non+disponible';
                    }}
                  />
                </ImageContainer>
              </div>
            );
          })}
        </Slider>
      )}
    </SliderContainer>
  );
};

export default SectorImageSlider;
